/**
 * Guards `Node.prototype.removeChild` / `insertBefore` against DOM mutations
 * made by Google Translate and similar extensions, which rewrap text nodes in
 * `<font>` elements and detach them from the parent React expects. Without the
 * guard, React's commit phase throws `NotFoundError: Failed to execute
 * 'removeChild' on 'Node'` and the route crashes to the error boundary.
 *
 * Only the would-have-thrown path changes behavior: when the child/reference
 * node is no longer attached to the expected parent, the call becomes a no-op
 * with a console.error (visible as a Sentry breadcrumb) instead of an
 * exception. https://github.com/facebook/react/issues/11538
 *
 * Call once from the app entry point, before rendering. No-op outside the
 * browser; accesses `Node` via globalThis so this compiles without DOM types.
 */
export function installDomResilience(): void {
  const NodeCtor = (globalThis as Record<string, any>).Node;
  if (typeof NodeCtor !== 'function' || !NodeCtor.prototype) {
    return;
  }
  const proto = NodeCtor.prototype;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function (child: any) {
    if (!child || child.parentNode !== this) {
      console.error('Skipped removeChild on detached node (translated DOM?)', child);
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function (newNode: any, reference: any) {
    if (reference && reference.parentNode !== this) {
      console.error('Skipped insertBefore with detached reference node (translated DOM?)', reference);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, reference);
  };
}
